import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HistoryIcon from '@mui/icons-material/History';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

import { RouteMap } from '@/routes/Routes';
import type { SecondBrainExperienceState } from '../hooks/useSecondBrainExperience';
import { formatRelativeTime } from '../utils/relativeTime';
import { AskComposer } from './AskComposer';
import { HistoryPalette } from './HistoryPalette';
import { SecondBrainChatView } from './SecondBrainChatView';
import { SecondBrainHome } from './SecondBrainHome';

type SecondBrainLayoutProps = SecondBrainExperienceState;

const isMacLike = /Mac|iPhone|iPad/.test(typeof navigator === 'undefined' ? '' : navigator.platform);
const PALETTE_SHORTCUT_LABEL = isMacLike ? '⌘K' : 'Ctrl K';

const KbdChip = ({ label }: { label: string }) => (
  <Box
    component="span"
    sx={{
      ml: 0.75,
      fontFamily: 'ui-monospace, Menlo, monospace',
      fontSize: 11,
      fontWeight: 500,
      color: 'grey.500',
      border: '1px solid',
      borderColor: 'neutral.otherBorder',
      borderRadius: '4px',
      px: 0.625,
      py: 0.375,
      lineHeight: 1,
    }}
  >
    {label}
  </Box>
);

const SecondBrainLayout = ({
  isPaletteOpen,
  availableTags,
  selectedTags,
  isLoadingTags,
  chatHistory,
  activeChatId,
  activeChat,
  isLoadingHistory,
  question,
  isStreaming,
  contextNotesPreview,
  displayedContextNotes,
  isFetchingContextNotes,
  onSelectChat,
  onTagsChange,
  onQuestionChange,
  onNewChat,
  onDeleteChat,
  onStartChat,
  onCancelChat,
  onOpenPalette,
  onClosePalette,
}: SecondBrainLayoutProps) => {
  const navigate = useNavigate();
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

  const handleOpenNote = useCallback(
    (noteId: string) => {
      navigate(`${RouteMap.NOTE}/${noteId}`);
    },
    [navigate],
  );

  const handleDeleteActiveChat = (event: React.MouseEvent) => {
    setMenuAnchorEl(null);
    if (activeChat) {
      void onDeleteChat(activeChat.id, event);
    }
  };

  const chatMeta = activeChat
    ? [
        activeChat.contextNotes?.length
          ? `${activeChat.contextNotes.length} note${activeChat.contextNotes.length === 1 ? '' : 's'}`
          : null,
        formatRelativeTime(activeChat.createdAt) || null,
      ]
        .filter(Boolean)
        .join(' · ')
    : '';

  const composerProps = {
    question,
    selectedTags,
    availableTags,
    isLoadingTags,
    isStreaming,
    isFindingNotes: isFetchingContextNotes && !isStreaming,
    contextNotesCount: contextNotesPreview.length,
    onQuestionChange,
    onTagsChange,
    onSubmit: () => void onStartChat(),
    onCancel: onCancelChat,
  };

  const historyButton = (
    <>
      <Button
        size="small"
        onClick={onOpenPalette}
        startIcon={<HistoryIcon sx={{ fontSize: 18 }} />}
        sx={{ color: 'text.secondary', display: { xs: 'none', sm: 'inline-flex' }, flex: 'none' }}
      >
        History
        <KbdChip label={PALETTE_SHORTCUT_LABEL} />
      </Button>
      <IconButton
        size="small"
        onClick={onOpenPalette}
        sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
        aria-label="Chat history"
      >
        <HistoryIcon sx={{ fontSize: 20 }} />
      </IconButton>
    </>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, bgcolor: 'background.default' }}>
      {/* Header */}
      <Box
        sx={{
          flex: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          height: 64,
          px: { xs: 2, sm: 3 },
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'neutral.otherBorder',
        }}
      >
        {activeChat ? (
          <>
            <IconButton size="small" onClick={onNewChat} aria-label="Back">
              <ArrowBackIcon sx={{ fontSize: 20 }} />
            </IconButton>
            <Stack sx={{ minWidth: 0 }} spacing={0}>
              <Typography sx={{ fontWeight: 600, fontSize: 15, lineHeight: '20px' }} noWrap>
                {activeChat.question ?? 'Second Brain'}
              </Typography>
              {chatMeta && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {chatMeta}
                </Typography>
              )}
            </Stack>
            <Box sx={{ flex: 1 }} />
            {historyButton}
            <Button
              size="small"
              variant="contained"
              onClick={onNewChat}
              startIcon={<AddIcon sx={{ fontSize: 18 }} />}
              sx={{ display: { xs: 'none', sm: 'inline-flex' }, flex: 'none' }}
            >
              New chat
            </Button>
            <IconButton
              size="small"
              onClick={(event) => setMenuAnchorEl(event.currentTarget)}
              aria-label="Chat actions"
            >
              <MoreHorizIcon sx={{ fontSize: 20 }} />
            </IconButton>
            <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={() => setMenuAnchorEl(null)}>
              <MenuItem onClick={handleDeleteActiveChat} sx={{ color: 'error.main' }}>
                <ListItemIcon>
                  <DeleteOutlineIcon fontSize="small" sx={{ color: 'error.main' }} />
                </ListItemIcon>
                <ListItemText>Delete chat</ListItemText>
              </MenuItem>
            </Menu>
          </>
        ) : (
          <>
            <Typography sx={{ flex: 1, fontWeight: 600, fontSize: 15, lineHeight: '20px' }}>Second Brain</Typography>
            {historyButton}
          </>
        )}
      </Box>

      {/* Body */}
      {activeChat ? (
        <SecondBrainChatView
          key={activeChat.id}
          activeChat={activeChat}
          isStreaming={isStreaming}
          sources={displayedContextNotes}
          isFetchingSources={isFetchingContextNotes}
          onOpenNote={handleOpenNote}
        />
      ) : (
        <SecondBrainHome
          chatHistory={chatHistory}
          composerSlot={<AskComposer variant="hero" {...composerProps} />}
          onSelectChat={onSelectChat}
          onOpenPalette={onOpenPalette}
        />
      )}

      {/* Follow-up composer (chat view only) */}
      {activeChat && (
        <Box
          sx={{
            flex: 'none',
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'neutral.otherBorder',
            p: { xs: '12px 16px 14px', sm: '12px 24px 16px' },
          }}
        >
          <Box sx={{ maxWidth: 760, mx: 'auto' }}>
            <AskComposer variant="followup" {...composerProps} />
          </Box>
        </Box>
      )}

      <HistoryPalette
        open={isPaletteOpen}
        chats={chatHistory}
        activeChatId={activeChatId}
        isLoading={isLoadingHistory}
        onClose={onClosePalette}
        onSelectChat={onSelectChat}
        onDeleteChat={onDeleteChat}
        onNewChat={onNewChat}
      />
    </Box>
  );
};

export default SecondBrainLayout;
